import { useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";

import {
  Container,
  HeaderSingle,
  HeaderMultiple,
  Form,
  AboutAnimals,
  ImagesAnimals,
  NamesAnimals,
  Content,
  CurrentAnimalData,
} from "./styles";
import { IAnimal } from "../../modules/animals/interface";
import { Input } from "../Input";
import { Button } from "../Button";
import {
  IManagedAnimal,
  IResultManagedAnimal,
} from "../../modules/management/interfaces";
import { useTranslate } from "../../hooks/useTranslate";

interface CardWeightCardProps {
  animals: IAnimal[];
  cb: (data: IManagedAnimal[]) => void;
  loading: boolean;
}

const AnimalWeightCard = ({ animals, cb, loading }: CardWeightCardProps) => {
  const { translate } = useTranslate();

  const [managedAnimals, setManagedAnimals] = useState<IManagedAnimal[]>([]);
  const [currentAnimal, setCurrentAnimals] = useState(0);
  const [allAnimals, setAllAnimals] = useState(animals);

  const schema = yup.object().shape({
    pesagem: yup.string().required(translate("management.weight_required")),
    diagnosticoDePrenhez: yup
      .string()
      .required(translate("global.description_required")),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IResultManagedAnimal>({
    resolver: yupResolver(schema),
  });

  const addManagedAnimal = (animal: IAnimal, data: IResultManagedAnimal) => {
    const managedAnimal: IManagedAnimal = {
      id: animal.id,
      name: animal.name,
      result: {
        diagnosticoDePrenhez: data.diagnosticoDePrenhez,
        pesagem: data.pesagem,
      },
    };

    setManagedAnimals((oldState) => [...oldState, managedAnimal]);
    return [...managedAnimals, managedAnimal];
  };

  const handleSubmitForm = (data: IResultManagedAnimal, id: string) => {
    const animal = allAnimals.find((animal) => animal.id === id);

    if (animal == null) return;

    const managedAnimalsIncremeted = addManagedAnimal(animal, data);

    if (currentAnimal + 1 < allAnimals.length) {
      setCurrentAnimals((oldState) => oldState + 1);
      reset();
      return;
    }

    cb(managedAnimalsIncremeted);
  };

  const handleRemoveAnimal = (id: string) => {
    const animalsFiltered = allAnimals.filter((animal) => animal.id !== id);

    if (animalsFiltered.length === currentAnimal) {
      cb(managedAnimals);
      return;
    }

    setAllAnimals(animalsFiltered);
  };

  const SingleAnimal = (animal: IAnimal) => (
    <>
      <HeaderSingle>
        {animal.number_identifier} - {animal.name}
      </HeaderSingle>
      <Form>
        <Input
          {...register("pesagem")}
          placeholder={translate("management.weight_animal")}
          type="number"
          error={errors.pesagem?.message}
        />
        <Input
          {...register("diagnosticoDePrenhez")}
          placeholder={translate("management.description")}
          error={errors.diagnosticoDePrenhez?.message}
        />

        <Button
          disabled={loading}
          label={translate("management.label_btn_save")}
          onClick={handleSubmit((data) => {
            handleSubmitForm(data, animal.id);
          })}
        />
      </Form>
    </>
  );

  const MultipleAnimals = () => {
    const isShowImagePlus = allAnimals.length - 3 > 0;
    return (
      <>
        <HeaderMultiple>
          <AboutAnimals>
            <ImagesAnimals>
              {allAnimals.slice(0, 3).map((animal) => (
                <img key={animal.id} src={animal.photo} alt={animal.name} />
              ))}
              {isShowImagePlus && <div>+{allAnimals.length - 3}</div>}
            </ImagesAnimals>
            <span>{allAnimals.length} animais</span>
          </AboutAnimals>
          <NamesAnimals>
            {allAnimals.map((animal, key) => (
              <span key={animal.id}>
                {animal.name}
                {key < allAnimals.length - 1 ? ", " : ""}
              </span>
            ))}
          </NamesAnimals>
        </HeaderMultiple>
        <Content>
          <CurrentAnimalData>
            <img
              src={allAnimals[currentAnimal].photo}
              alt={allAnimals[currentAnimal].name}
            />
            <p>
              {allAnimals[currentAnimal].number_identifier} -{" "}
              {allAnimals[currentAnimal].name}
            </p>
            <span>
              {currentAnimal + 1} de {allAnimals.length}
            </span>
          </CurrentAnimalData>
          <Form>
            <Input
              {...register("pesagem")}
              placeholder={translate("management.weight_animal")}
              type="number"
              error={errors.pesagem?.message}
            />
            <Input
              {...register("diagnosticoDePrenhez")}
              placeholder={translate("management.description")}
              error={errors.diagnosticoDePrenhez?.message}
            />

            <Button
              type="button"
              label={translate("management.remove_animal")}
              onClick={() => {
                handleRemoveAnimal(allAnimals[currentAnimal].id);
              }}
            />
            <Button
              disabled={loading}
              label={translate("management.label_btn_save")}
              onClick={handleSubmit((data) => {
                handleSubmitForm(data, allAnimals[currentAnimal].id);
              })}
            />
          </Form>
        </Content>
      </>
    );
  };

  return (
    <Container>
      {allAnimals.length === 1 && <SingleAnimal {...allAnimals[0]} />}
      {allAnimals.length > 1 && <MultipleAnimals />}
    </Container>
  );
};

export { AnimalWeightCard };
